<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Time Card" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            class="hide-on-print"
            :block="true"
            @click="handleExportToExcel"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <TimeCardReportFilters
        ref="timeCardReportFilters"
        class="px-5 mb-sm-5"
        @clearFilters="() => showNoDataMessage = true"
        @searchTimeCardEntries="searchTimeCardEntriesHandler"
      ></TimeCardReportFilters>
    </div>
    <div v-if="!showNoDataMessage">
      <ReportHeader
        reportTitle="Time Card Report"
        :reportCriteria="reportFilterCriteria"
      ></ReportHeader>
      <v-row class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <HoursDetails
                class="py-3 px-5"
                :totalBillableHours="timeCardReport.totalBillableHours"
                :stHours="timeCardReport.stHours"
                :otHours="timeCardReport.otHours"
              ></HoursDetails>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <InvoicingDetails
                class="py-3 px-5"
                :clientName="timeCardReport.assignment.clientName"
                :totalBillAmount="timeCardReport.totalBillAmount"
                :totalBillableHours="timeCardReport.totalBillableHours"
                :invoiceFrequencyId="timeCardReport.assignment.invoiceFrequencyId"
                :billRateTypeId="timeCardReport.assignment.billRateTypeId"
                :billRate="timeCardReport.assignment.billRate"
                :showStandardTime="showStandardTime"
                :showOvertime="isOvertimeEligible"
              ></InvoicingDetails>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <PayrollDetails
                class="py-3 px-5"
                :consultantName="timeCardReport.assignment.userFullName"
                :totalPayAmount="timeCardReport.totalPayAmount"
                :payFrequencyId="timeCardReport.assignment.payFrequencyId"
                :payRateTypeId="timeCardReport.assignment.payRateTypeId"
                :payRate="timeCardReport.assignment.payRate"
                :showStandardTime="showStandardTime"
                :showOvertime="isOvertimeEligible"
              ></PayrollDetails>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-container fluid class="px-0 pt-2 mt-4">
        <v-layout column>
          <v-data-table
            dense
            :headers="headers"
            :items="timeCardReport.timeCardEntries"
            class="elevation-2"
          >
            <template v-slot:item.date="{ item }">
              {{ item.date | formatDate }}
            </template>
            <template v-slot:item.day="{ item }">
              {{ item.date | dayOfWeekString }}
            </template>
            <template v-slot:item.otPayRate="{ item }">
              {{ item.otPayRate | currency }}
            </template>
            <template v-slot:item.otBillRate="{ item }">
              {{ item.otBillRate | currency }}
            </template>
            <template v-slot:item.totalPay="{ item }">
              {{ item.totalPay | currency }}
            </template>
            <template v-slot:item.totalBill="{ item }">
              {{ item.totalBill | currency }}
            </template>
          </v-data-table>
        </v-layout>
      </v-container>
    </div>
    <div class="mt-5" v-if="showNoDataMessage">
      <NoDataAvailableMessage></NoDataAvailableMessage>
    </div>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { SimplePageHeader } from '@/components/pageComponents'
import { BottomSpacer, ButtonCustomIcon, ButtonPrint } from '@/components/buttons'
import {
  PayrollDetails,
  InvoicingDetails,
  HoursDetails
} from '@/components/timeCardReport/index'
import TimeCardReportFilters from '@/components/timeCardReport/TimeCardReportFilters'
import { mapFields } from '@/vueHelpers/vuex'
import NoDataAvailableMessage from '@/components/reports/NoDataAvailableMessage'
import { HOURLY as HOURLY_PAY_RATE } from '@/lib/constants/payRateTypes'
import { HOURLY as HOURLY_BILL_RATE } from '@/lib/constants/billRateTypes'
import { mapActions } from 'vuex'
import { formatCurrency, formatDate, dayOfWeekString } from '@/lib/display'
import excel from '@/lib/export/excel'
import { STAFF, CONSULTANTS } from '@/lib/constants/reportTypes'
import ReportHeader from '@/components/reports/ReportHeader'

export default {
  components: {
    NoDataAvailableMessage,
    SimplePageHeader,
    PayrollDetails,
    InvoicingDetails,
    HoursDetails,
    TimeCardReportFilters,
    BottomSpacer,
    ButtonCustomIcon,
    ButtonPrint,
    ReportHeader
  },
  data () {
    return {
      STAFF,
      CONSULTANTS,
      showNoDataMessage: true
    }
  },
  computed: {
    ...mapFields('timeCardReport', ['timeCardReport']),
    ...mapFields('timeCardReportFilters', [
      'reportFilterType',
      'startDate',
      'endDate',
      'clientId',
      'userId',
      'assignmentId'
    ]),
    reportFilterCriteria: {
      get () {
        return [
          { 'key': 'Date Begin', 'value': formatDate(this.startDate) },
          { 'key': 'Date End', 'value': formatDate(this.endDate) }
        ]
      }
    },
    headers: {
      get () {
        let headers = [
          { text: 'Date', value: 'date', sortable: false, align: 'center' },
          { text: 'Day', value: 'day', sortable: false, align: 'start' },
          { text: 'Time Sheet Hours', value: 'timeSheetHours', sortable: false, align: 'center' }
        ]
        if (this.isOvertimeEligible) {
          headers.push({ text: 'OT', value: 'otHours', sortable: false, align: 'center' })
          headers.push({ text: 'OT Pay Rate', value: 'otPayRate', sortable: false, align: 'center', class: 'hide-on-print', cellClass: 'hide-on-print' })
          headers.push({ text: 'OT Bill Rate', value: 'otBillRate', sortable: false, align: 'center' })
        }
        if (this.isHourlyPaid) {
          headers.push({ text: 'Total Pay', value: 'totalPay', sortable: false, align: 'center', class: 'hide-on-print', cellClass: 'hide-on-print' })
        }
        if (this.isHourlyBilled) {
          headers.push({ text: 'Total Bill', value: 'totalBill', sortable: false, align: 'center' })
        }
        return headers
      }
    },
    showStandardTime () {
      return (this.timeCardReport.timeCardEntries && this.timeCardReport.timeCardEntries.length > 0) ||
          (this.isHourlyPaid || this.isHourlyBilled)
    },
    isHourlyPaid () {
      return this.timeCardReport.assignment.payRateTypeId === HOURLY_PAY_RATE
    },
    isHourlyBilled () {
      return this.timeCardReport.assignment.billRateTypeId === HOURLY_BILL_RATE
    },
    isOvertimeEligible: {
      get () {
        return this.timeCardReport.assignment && this.timeCardReport.assignment.overtimeEligible
      }
    },
    isDisplayingConsultants () {
      return this.reportFilterType === this.CONSULTANTS
    }
  },
  methods: {
    ...mapActions('timeCardReport', [
      'getTimeCardReportWithFilters'
    ]),
    ...mapActions('timeCardReportFilters', ['clearFilters']),
    handleExportToExcel () {
      if (this.timeCardReport &&
        this.timeCardReport.timeCardEntries &&
        this.timeCardReport.timeCardEntries.length > 0) {
        let data = []
        for (let entry of this.timeCardReport.timeCardEntries) {
          data.push({
            date: formatDate(entry.date),
            day: dayOfWeekString(entry.date),
            timeSheetHours: entry.timeSheetHours,
            payRate: formatCurrency(entry.payRate),
            billRate: formatCurrency(entry.billRate),
            otHours: entry.otHours,
            otPayRate: formatCurrency(entry.otPayRate),
            otBillRate: formatCurrency(entry.otBillRate),
            totalPay: formatCurrency(entry.totalPay),
            totalBill: formatCurrency(entry.totalBill)
          })
        }

        const params = {
          title: ['Date', 'Day', 'Time Sheet Hours', 'Pay Rate', 'Bill Rate', 'OT', 'OT Pay Rate', 'OT Bill Rate', 'Total Pay', 'Total Bill'],
          key: ['date', 'day', 'timeSheetHours', 'payRate', 'billRate', 'otHours', 'otPayRate', 'otBillRate', 'totalPay', 'totalBill'],
          data: data,
          autoWidth: true,
          filename: `TimeCard`
        }
        excel.exportArray2Excel(params)
      }
    },
    searchTimeCardEntriesHandler () {
      this.showNoDataMessage = !this.timeCardReport.assignment.id
    }
  },
  async mounted () {
    if (this.$route.query.startDate) {
      this.startDate = this.$route.query.startDate
    }
    if (this.$route.query.endDate) {
      this.endDate = this.$route.query.endDate
    }
    if (this.$route.query.clientId) {
      await this.$refs.timeCardReportFilters.handleClientIdChange(parseInt(this.$route.query.clientId)).then(() => {
        if (this.$route.query.userId) {
          this.$refs.timeCardReportFilters.handleUserIdChange(parseInt(this.$route.query.userId))
        }
        if (this.$route.query.assignmentId) {
          this.assignmentId = parseInt(this.$route.query.assignmentId)
        }
      })
    }
    if (
      (this.clientId && this.userId && this.assignmentId && this.startDate && this.endDate)) {
      await this.getTimeCardReportWithFilters().then(() => {
        this.showNoDataMessage = false
      })
    } else {
      this.clearFilters()
      this.showNoDataMessage = true
    }
  }
}
</script>
